/** An option of a radio group as its keyboard reads it. */
export interface RovingOption<V> {
  value: V;
  disabled?: boolean;
}

function enabledAt<V>(options: readonly RovingOption<V>[], index: number, groupDisabled: boolean): boolean {
  return !groupDisabled && !options[index]?.disabled;
}

/**
 * The one Tab stop of a radio group (WAI-ARIA APG radio group, `ChipGroup
 * single` and `SegmentedControl`): the checked option when it is listed and
 * enabled; otherwise the first enabled one, so a group whose `value` is not
 * among its options (a view whose option just went away) or is disabled never
 * drops out of the Tab order. -1 when no option is enabled.
 */
export function rovingTabStop<V>(
  options: readonly RovingOption<V>[],
  value: V | null | undefined,
  groupDisabled = false,
): number {
  const checked = options.findIndex((option) => option.value === value);
  if (checked >= 0 && enabledAt(options, checked, groupDisabled)) return checked;
  return options.findIndex((_, index) => enabledAt(options, index, groupDisabled));
}

/**
 * Where an arrow key goes from option `from` (the focused one, or the Tab
 * stop): the next enabled option in `step`'s direction, wrapping around.
 * -1 when no option is enabled.
 */
export function rovingStep<V>(
  options: readonly RovingOption<V>[],
  from: number,
  step: 1 | -1,
  groupDisabled = false,
): number {
  const count = options.length;
  for (let i = 1; i <= count; i++) {
    const next = (((from + step * i) % count) + count) % count;
    if (enabledAt(options, next, groupDisabled)) return next;
  }
  return -1;
}
